import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Register() {
  const { t } = useTranslation();

  const [organizationName, setOrganizationName] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantLocation, setRestaurantLocation] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const inputClassName = "w-full px-3.5 py-2.5 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1a4731]/25 focus:border-[#1a4731]/40 transition-all";
  const labelClassName = "block text-sm font-medium text-slate-600 mb-1.5";

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError(t('register.passwordsMismatch'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/signup-requests/`, {
        organization_name: organizationName || restaurantName,
        restaurant_name: restaurantName,
        restaurant_location: restaurantLocation,
        email,
        password,
        password_confirm: passwordConfirm,
        first_name: firstName,
        last_name: lastName,
        note,
      });
      setSubmitted(true);
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        const messages = [];
        for (const value of Object.values(data)) {
          const vals = Array.isArray(value) ? value : [value];
          messages.push(...vals.map(v => (typeof v === 'string' ? v : JSON.stringify(v))));
        }
        setError(messages.join(' '));
      } else {
        setError(t('register.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f8f7f4]">
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-12 relative overflow-hidden bg-[#1a4731]">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-16 w-44 h-44 rounded-full bg-white/[0.03]" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/10">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-bold text-xl tracking-tight">Carlo</span>
        </div>

        <div className="relative z-10">
          <h1 className="text-5xl font-bold text-white leading-[1.1] mb-5 tracking-tight">
            Smart<br />Restaurant<br />Operations
          </h1>
          <p className="text-white/50 text-lg leading-relaxed max-w-xs">
            Manage your sales, inventory, staff and analytics — all in one place.
          </p>
        </div>

        <p className="text-white/25 text-sm relative z-10">© {new Date().getFullYear()} Carlo</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-[420px]">
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div className="w-11 h-11 bg-[#1a4731] rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900 tracking-tight">Carlo</span>
          </div>

          {submitted ? (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-1.5">Request sent</h2>
              <p className="text-slate-400 text-sm mb-8">
                The team will review your request. If accepted, you can sign in with this email and password.
              </p>
              <Link
                to="/login"
                className="block w-full py-2.5 text-center bg-[#1a4731] hover:bg-[#153a27] text-white font-semibold rounded-lg text-sm transition-colors"
              >
                {t('register.loginLink')}
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-900 mb-1.5">Request access</h2>
              <p className="text-slate-400 text-sm mb-8">{t('register.subtitle')}</p>

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label className={labelClassName}>
                    {t('register.organizationName')}
                  </label>
                  <input
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    className={inputClassName}
                    placeholder={t('register.organizationNamePlaceholder')}
                  />
                  <p className="text-xs text-slate-400 mt-1">{t('register.organizationNameHint')}</p>
                </div>

                <div>
                  <label className={labelClassName}>
                    {t('register.restaurantName')}
                  </label>
                  <input
                    type="text"
                    value={restaurantName}
                    onChange={(e) => setRestaurantName(e.target.value)}
                    className={inputClassName}
                    placeholder={t('register.restaurantNamePlaceholder')}
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className={labelClassName}>
                    {t('register.restaurantLocation')}
                  </label>
                  <input
                    type="text"
                    value={restaurantLocation}
                    onChange={(e) => setRestaurantLocation(e.target.value)}
                    className={inputClassName}
                    placeholder={t('register.restaurantLocationPlaceholder')}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClassName}>
                      {t('register.firstName')}
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                  <div>
                    <label className={labelClassName}>
                      {t('register.lastName')}
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClassName}>
                    {t('register.email')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClassName}
                    required
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className={labelClassName}>
                    {t('login.password')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClassName}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className={labelClassName}>
                    {t('register.confirmPassword')}
                  </label>
                  <input
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    className={inputClassName}
                    required
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className={labelClassName}>
                    Note
                  </label>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className={`${inputClassName} resize-y min-h-[92px]`}
                    rows={3}
                    placeholder="Restaurant size, location, or what you want Carlo to help with"
                  />
                </div>

                {error && (
                  <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-[#1a4731] hover:bg-[#153a27] text-white font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Sending...' : 'Request access'}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-400">
                {t('register.hasAccount')}{' '}
                <Link to="/login" className="text-[#1a4731] hover:text-[#153a27] font-medium transition-colors">
                  {t('register.loginLink')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
